/* eslint-disable no-unused-vars */
/* eslint-disable react/display-name */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { getAssetIcon } from 'helpers/icons';
import { filteredPowertrain, filteredAssetGroup, iconCondition, handleRemoveQueryParams } from './helpers';
import { useTranslation } from 'react-i18next';
import { assetActive, LEFT_PANEL_WIDTH_PERCENT, MAX_CHAR_LENGTH } from './constants';
import { cancel as cancelLimitChanges } from '../../PowertrainAssetDetails/DetailedInformation/LimitConfigurationBeta/actions';
import UnSavedChangesModal from '../../PowertrainAssetDetails/DetailedInformation/LimitConfigurationBeta/components/UnSavedChangesModal';
import useUnsavedChanges from 'sharedHooks/useUnsavedChanges';
import { useHistory } from 'react-router';
import { routes } from 'routes';
import TrendViewComponent from './TrendViewComponent';
import ThresholdViewComponent from './ThresholdViewComponent';
import AssetLimitsSummaryComponent from './AssetLimitsSummaryComponent';
import TooltipTitleComponent from './TooltipTitleComponent';

const AssetRowComponent = React.memo(({
    asset,
    selectedAsset,
    onSelectedItem,
    allAssets,
    // onAssetToggle,
    showThreshold,
    showTrend,
    isHorizontalScrollActive,
    powertrainList,
    selectedPowertrain,
    assetGroupList,
    selectedAssetGroup,
    isLabelVisible,
    leftPanelSize,
    index,
    indentPadding,
    hasSelectionDot,
    selectionCount
}) => {
    const { t: translate } = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();
    const assetName = asset?.assetName || asset?.name;
    const isLengthyTitle = assetName?.length > MAX_CHAR_LENGTH;
    const isSelected = selectedAsset?.assetId === asset?.assetId;
    const [wasAutoSelected, setWasAutoSelected] = useState(false);
    // Local state for tracking pending asset changes
    const [pendingAsset, setPendingAsset] = React.useState(null);

    // Redux state selectors
    // const operationalParametersV2State = useSelector(state => state.operationalParameters);

    // Check for unsaved changes in limit configuration (same as TabsV2)
    const hasUnsavedLimitChanges = useSelector(({ limitConfig }) =>
        limitConfig?.currentTimeSeries?.some(item =>
            item?.isModified || item?.limits?.some(limit => limit?.isModified)
        ) || false
    );

    // Track if this asset was auto-selected via search
    useEffect(() => {
        if (isSelected && selectedAsset) {
            setWasAutoSelected(true);
            // Remove the auto-selected indicator after 2 seconds
            const timer = setTimeout(() => setWasAutoSelected(false), 2000);
            return () => clearTimeout(timer);
        } else {
            setWasAutoSelected(false);
        }
    }, [isSelected, selectedAsset]);


    // Use the same hook pattern as TabsV2 (PROVEN WORKING)
    const {
        showUnsavedModal,
        setShowUnsavedModal,
        checkUnsavedChanges,
        handleDiscardAndContinue: originalHandleDiscardAndContinue,
    } = useUnsavedChanges(hasUnsavedLimitChanges);

    // Check if limit configuration mode is active (to avoid modal conflicts)
    // const isLimitConfigActive = operationalParametersV2State?.isInternalTabLimitActive || false;

    // Custom discard handler that resets limit config state and executes pending action
    const handleDiscardAndContinue = () => {
        dispatch(cancelLimitChanges());
        originalHandleDiscardAndContinue();

        // Execute pending asset change if any
        if (pendingAsset) {
            onSelectedItem(pendingAsset);
            setPendingAsset(null);
        }
    };

    const handleAssetNavigation = (e, selectedAssetId) => {
        e.stopPropagation();
        const assetId = selectedAssetId?.assetId;
        let routeData;
        const filteredPowertrainList = filteredPowertrain(powertrainList, selectedPowertrain);
        const filteredAssetGroupList = filteredAssetGroup(assetGroupList, selectedAssetGroup);
        if (selectedPowertrain && filteredPowertrainList?.length) {
            routeData = filteredPowertrainList;
        }
        else if (selectedAssetGroup && filteredAssetGroupList?.length) {
            routeData = filteredAssetGroupList;
        } else {
            routeData = { assets: [...allAssets] };
        }
        history.push({
            pathname: routes?.OperationalParametersV2,
            search: new URLSearchParams({ assetId })?.toString(),
            state: {
                routeName: translate('ABB.Powertrain.Frontend.ptConditionMonitoringPowertrainsAssetsOverviewTab'),
                routeData,
            },
        });
    };

    const handleAssetClick = (asset, e) => {
        e.stopPropagation();
        handleRemoveQueryParams(history);
        // Check if switching to different asset
        const isDifferentAsset = selectedAsset?.assetId !== asset?.assetId;

        // If same asset, just toggle selection
        if (!isDifferentAsset) {
            onSelectedItem(null);
            return;
        }
        // If modal is already showing, don't proceed
        if (showUnsavedModal) {
            return;
        }

        // Define what happens when asset switches
        const performAssetSwitch = () => {
            onSelectedItem(asset);
        };
        // Store pending asset for manual modal handling if needed
        setPendingAsset(asset);

        const wasExecuted = checkUnsavedChanges(performAssetSwitch);
        // Clear pending asset if action was executed immediately
        if (wasExecuted) {
            setPendingAsset(null);
        }
    };

    // Create dynamic className for the item
    const itemClassName = `item ${isSelected ? assetActive.ACTIVE : ''} ${wasAutoSelected ? 'auto-selected' : ''}`;

    return (
        <>
            {showUnsavedModal &&
                <UnSavedChangesModal
                    showModal={showUnsavedModal}
                    setShowModal={setShowUnsavedModal}
                    handleDiscardAndContinue={handleDiscardAndContinue}
                    isResetToDefault={false}
                />
            }
            <button
                className={itemClassName}
                onClick={(e) => handleAssetClick(asset, e)}
                style={indentPadding ? { paddingLeft: `${indentPadding + 24}px` } : undefined}
                type='button'>
                <div
                    className={`cell-fixed ${isHorizontalScrollActive ? 'horizontal-scroll-active' : ''}`}
                    style={indentPadding ? { maxWidth: `${698 - indentPadding}px` } : undefined}
                >
                    <div className='item-status'>
                        <span className={`asset-condition ${iconCondition(asset?.condition)}`}></span>
                    </div>
                    <div className='item-name' style={leftPanelSize <= 50 ? { minWidth: '150px !important' } : {}}>
                        <div className='item-icon'>
                            {getAssetIcon(asset?.assetType, { width: 20, height: 20 })}
                        </div>
                        <div className={`name d-flex ${isLengthyTitle ? '' : 'text-truncate'}`}>
                            {isLengthyTitle ? <TooltipTitleComponent index={index} title={assetName} leftPanelSize={leftPanelSize} />
                                : <div className='text-truncate'>{assetName}</div>}
                        </div>
                    </div>
                </div>
                {hasSelectionDot &&
                    <span className='selection-dot' aria-hidden='true'>
                        {selectionCount > 0 && selectionCount}
                    </span>
                }
                {showThreshold && leftPanelSize >= LEFT_PANEL_WIDTH_PERCENT ? <ThresholdViewComponent item={asset} isLabelVisible={isLabelVisible} leftPanelSize={leftPanelSize}></ThresholdViewComponent> : ''}
                {showTrend && leftPanelSize >= LEFT_PANEL_WIDTH_PERCENT ? <TrendViewComponent item={asset} isLabelVisible={isLabelVisible} leftPanelSize={leftPanelSize}></TrendViewComponent> : ''}
                {leftPanelSize >= LEFT_PANEL_WIDTH_PERCENT && <AssetLimitsSummaryComponent item={asset} isLabelVisible={isLabelVisible} leftPanelSize={leftPanelSize}></AssetLimitsSummaryComponent>}
            </button>
        </>
    );
});

AssetRowComponent.propTypes = {
    asset: PropTypes.shape({
        assetId: PropTypes.number,
        assetType: PropTypes.string,
        assetName: PropTypes.string,
        condition: PropTypes.string,
    }),
    selectedAsset: PropTypes.object,
    onSelectedItem: PropTypes.func,
    showThreshold: PropTypes.bool,
    showTrend: PropTypes.bool,
    isHorizontalScrollActive: PropTypes.bool,
    allAssets: PropTypes.array,
    onAssetToggle: PropTypes.func,
    powertrainList: PropTypes.array,
    selectedPowertrain: PropTypes.number,
    assetGroupList: PropTypes.array,
    selectedAssetGroup: PropTypes.number,
    isLabelVisible: PropTypes.bool,
    indentPadding: PropTypes.number,
    hasSelectionDot: PropTypes.bool,
    selectionCount: PropTypes.number
};

export default AssetRowComponent;
